import { useEffect, useState } from 'react'
import { getPlanningYears } from '@/api/planning.api'
import type { PlanningYearDto } from '@/api/dto/planning.dto'

export type PlanningYears = {
  /** False until GET /planning-years answered or failed. */
  loaded: boolean
  currentYear: number
  /** Years SIBs may plan capacities and orders into today, earliest first. */
  openYears: number[]
  isOpen: (year: number) => boolean
  /** The ISO date a year opens, when Union has scheduled it; null otherwise. */
  unlockedFrom: (year: number) => string | null
}

/**
 * Which years are open for planning (SIB-928). Union opens the next year in the backoffice, so the
 * backend decides - never a fixed date in the browser. Until the answer arrives, and if it fails,
 * only the running year is open.
 */
export function usePlanningYears(): PlanningYears {
  const fallbackYear = new Date(Date.now()).getFullYear()
  const [state, setState] = useState<{
    loaded: boolean
    currentYear: number
    openYears: number[]
    years: PlanningYearDto[]
  }>({ loaded: false, currentYear: fallbackYear, openYears: [fallbackYear], years: [] })

  useEffect(() => {
    let cancelled = false
    getPlanningYears()
      .then(res => {
        if (cancelled) return
        setState(prev => ({
          loaded: true,
          // Keep the browser's year if the answer lacks one, so no caller gets undefined.
          currentYear: res.currentYear ?? prev.currentYear,
          openYears: [...(res.openYears ?? [])].sort((a, b) => a - b),
          years: res.years ?? [],
        }))
      })
      .catch(() => {
        if (!cancelled) setState(prev => ({ ...prev, loaded: true }))
      })
    return () => {
      cancelled = true
    }
  }, [])

  return {
    loaded: state.loaded,
    currentYear: state.currentYear,
    openYears: state.openYears,
    isOpen: year => state.openYears.includes(year),
    unlockedFrom: year => state.years.find(y => y.year === year)?.unlockedFrom ?? null,
  }
}
